'use client';

import { BulkUploadProcessor } from './_components/BulkUploadProcessor';
import { DocumentChecklist, Flex, Loading, Text } from '@/_components';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { BulkUploadProcessorProvider } from '@/_contexts/admin/BulkUploadProcessorProvider';
import { getAllDocumentChecklistItems } from '@/_utils/questionnaire';
import { getUploadedItems } from '@/_utils/answers';
import { useMemo, useContext, useEffect, useState, type FC } from 'react';
import type { DocumentsPreviewTabProps } from './DocumentsPreviewTab.types';

export const DocumentsPreviewTab: FC<DocumentsPreviewTabProps> = ({
  questionnaire,
  isLoadingQuestionnairePreview,
  reloadQuestionnairePreview,
  isBulkUploadProcessing,
  setIsBulkUploadProcessing,
  isBUPReviewDialogOpenForSource,
  setIsBUPReviewDialogOpenForSource,
}) => {
  const { preferences } = useContext(FirmSettingsContext);

  const allDocumentsNeededAsQuestions = useMemo(
    () => getAllDocumentChecklistItems(questionnaire),
    [questionnaire],
  );

  const [documentsFromAnswers, setDocumentsFromAnswers] = useState<Array<any>>(
    [],
  );
  const [didAtLeastOneDrag, setDidAtLeastOneDrag] = useState(false);

  // TODO @chris this needs to be stateless instead
  useEffect(() => {
    const updatedDocuments = allDocumentsNeededAsQuestions.reduce(
      (acc: any, question: any) => {
        let documents = null;

        if (question.answer) {
          documents = getUploadedItems(question.answer);
        }

        if (
          !acc.some((hash: any) => Object.keys(hash)[0] === String(question.id))
        ) {
          acc.push({ [question.id]: documents || [] });
        }

        return acc;
      },
      [],
    );

    setDocumentsFromAnswers(updatedDocuments);
  }, [allDocumentsNeededAsQuestions]);

  useEffect(() => {
    return () => {
      if (didAtLeastOneDrag) {
        reloadQuestionnairePreview();
        setDidAtLeastOneDrag(false);
      }
    };
  }, [didAtLeastOneDrag, reloadQuestionnairePreview, setDidAtLeastOneDrag]);

  const hasDocumentChecklist: boolean =
    (allDocumentsNeededAsQuestions.length > 0 &&
      documentsFromAnswers.length > 0) ||
    (!!questionnaire?.sections?.some((s) => s.section_type === 10) &&
      (questionnaire?.extracted_documents?.length ?? 0) > 0);

  return (
    <Flex isColumn={true} gap={8}>
      {preferences?.bulk_upload_processor && (
        <BulkUploadProcessorProvider>
          <BulkUploadProcessor
            questionnaire={questionnaire}
            reloadQuestionnairePreview={reloadQuestionnairePreview}
            isBulkUploadProcessing={isBulkUploadProcessing}
            setIsBulkUploadProcessing={setIsBulkUploadProcessing}
            isBUPReviewDialogOpenForSource={isBUPReviewDialogOpenForSource}
            setIsBUPReviewDialogOpenForSource={
              setIsBUPReviewDialogOpenForSource
            }
          />
        </BulkUploadProcessorProvider>
      )}
      <>
        {hasDocumentChecklist ? (
          <DocumentChecklist
            documentsFromAnswers={documentsFromAnswers}
            mode="admin-preview"
            loadQuestionnaire={reloadQuestionnairePreview}
            questionnaire={questionnaire}
            setDidAtLeastOneDrag={setDidAtLeastOneDrag}
            setDocumentsFromAnswers={setDocumentsFromAnswers}
          />
        ) : (
          <>
            {isLoadingQuestionnairePreview ? (
              <Loading size={20} />
            ) : (
              <Text type="small">
                Documents were not requested for the questionnaire.
              </Text>
            )}
          </>
        )}
      </>
    </Flex>
  );
};
